import { useState, useEffect, useLayoutEffect, useRef } from "react"
import { StaticImage } from "gatsby-plugin-image"
import { ChevronDownIcon } from "@heroicons/react/24/outline"

declare global {
  interface Window {
    ___navigate?: (path: string) => void
  }
}

const START_COVER_HEADING = "Annual report"
const START_COVER_DESCRIPTION = "Key figures and highlights."
const START_COVER_CTA = "Start reading"
const READ_OLDER_REPORTS = "Read older reports"
const OLDER_REPORTS_SECTION_ID = "download-report"
const START_READING_TRANSITION_MS = 900
const REPORT_TRANSITION_FLAG_KEY = "annualReportHeroTransition"
const REPORT_TITLE_SETTLE_MS = 650
const REPORT_ARROW_REVEAL_DELAY_MS = 1050

const HERO_SECTION_BASE_CLASS =
  "relative flex min-h-screen min-h-[100svh] flex-col items-center overflow-hidden bg-zinc-900 px-4 pb-16 transition-[padding] duration-700 ease-out sm:px-6 lg:px-8"
const HERO_CONTENT_WRAPPER_CLASS =
  "relative z-10 flex w-full max-w-3xl flex-col items-center text-center"
const HERO_TITLE_CLASS =
  "text-3xl font-bold tracking-tight text-white drop-shadow-md sm:text-4xl lg:text-5xl"
const HERO_DESCRIPTION_BASE =
  "text-base text-white/90 drop-shadow-sm sm:text-lg"
const START_READING_PATH = "/schools/annual-reports/2025/"

const getLocalizedStartReadingPath = () => {
  if (typeof window === "undefined") return START_READING_PATH
  const localeMatch = window.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})(\/|$)/i)
  const localePrefix = localeMatch?.[1] ? `/${localeMatch[1]}` : ""
  return `${localePrefix}${START_READING_PATH}`
}

interface HeroSharedProps {
  title?: string
  description?: string
  noteText?: string
  imageAnimate?: boolean
  descriptionFadeIn?: boolean
  children?: React.ReactNode
}

interface HeroCoverProps extends HeroSharedProps {
  scrollTargetId?: null
  gradientFrom?: "zinc-900" | "zinc-800"
  startCoverCta?: string
  readOlderReports?: string
  showOlderReportsButton?: boolean
  showScrollArrow?: never
  scrollArrowDelay?: never
}

interface HeroReportProps extends HeroSharedProps {
  scrollTargetId: string
  showScrollArrow?: boolean
  scrollArrowDelay?: number
  gradientFrom?: never
  startCoverCta?: never
  readOlderReports?: never
  showOlderReportsButton?: never
}

type HeroProps = HeroCoverProps | HeroReportProps

const Hero = ({
  title = START_COVER_HEADING,
  description = START_COVER_DESCRIPTION,
  noteText = "",
  startCoverCta = START_COVER_CTA,
  readOlderReports = READ_OLDER_REPORTS,
  showOlderReportsButton = true,
  gradientFrom = "zinc-800",
  scrollTargetId = null,
  descriptionFadeIn = false,
  scrollArrowDelay = 600,
  imageAnimate = true,
  showScrollArrow = true,
  children,
}: HeroProps) => {
  const [imageVisible, setImageVisible] = useState(!imageAnimate)
  const [descriptionVisible, setDescriptionVisible] =
    useState(!descriptionFadeIn)
  const [bodyVisible, setBodyVisible] = useState(true)
  const [bodyFadeVisible, setBodyFadeVisible] = useState(!scrollTargetId)
  const [startTransitioning, setStartTransitioning] = useState(false)
  const [reportEntryAnimating, setReportEntryAnimating] = useState(false)
  const [reportTitleSettled, setReportTitleSettled] = useState(true)
  const [scrollArrowVisible, setScrollArrowVisible] = useState(!scrollTargetId)
  const [contentFitsViewport, setContentFitsViewport] = useState(false)
  const contentWrapperRef = useRef<HTMLDivElement>(null)
  const childrenRef = useRef<HTMLDivElement>(null)
  const isReportVariant = Boolean(scrollTargetId)

  useEffect(() => {
    if (imageAnimate) {
      const t = setTimeout(() => setImageVisible(true), 50)
      return () => clearTimeout(t)
    }
  }, [imageAnimate])

  useEffect(() => {
    if (descriptionFadeIn) {
      const t = setTimeout(() => setDescriptionVisible(true), 150)
      return () => clearTimeout(t)
    }
  }, [descriptionFadeIn])

  useLayoutEffect(() => {
    if (!isReportVariant || typeof window === "undefined") {
      setBodyVisible(true)
      setBodyFadeVisible(true)
      setReportEntryAnimating(false)
      setReportTitleSettled(true)
      setScrollArrowVisible(!scrollTargetId)
      return
    }

    const shouldAnimateEntry =
      window.sessionStorage.getItem(REPORT_TRANSITION_FLAG_KEY) === "1"
    window.sessionStorage.removeItem(REPORT_TRANSITION_FLAG_KEY)

    if (!shouldAnimateEntry) {
      setReportEntryAnimating(false)
      setReportTitleSettled(true)
      setBodyVisible(true)
      const t = setTimeout(() => setScrollArrowVisible(true), scrollArrowDelay)
      return () => clearTimeout(t)
    }

    setReportEntryAnimating(true)
    setReportTitleSettled(true)
    setBodyVisible(true)
    setScrollArrowVisible(false)
    const revealArrowTimer = setTimeout(
      () => setScrollArrowVisible(true),
      REPORT_ARROW_REVEAL_DELAY_MS,
    )

    return () => {
      clearTimeout(revealArrowTimer)
    }
  }, [isReportVariant, scrollTargetId, scrollArrowDelay])

  useEffect(() => {
    if (!isReportVariant || typeof window === "undefined") {
      setBodyFadeVisible(true)
      return
    }
    // Always fade report body in on mount/navigation to report hero.
    setBodyFadeVisible(false)
    const fadeFrame = requestAnimationFrame(() => {
      setBodyFadeVisible(true)
    })
    return () => cancelAnimationFrame(fadeFrame)
  }, [isReportVariant])

  useLayoutEffect(() => {
    if (!isReportVariant || typeof window === "undefined") return
    const ARROW_CLEARANCE_PX = 96
    const checkFit = () => {
      if (!childrenRef.current) return
      const bottom = childrenRef.current.getBoundingClientRect().bottom
      const fits = bottom <= window.innerHeight - ARROW_CLEARANCE_PX
      setContentFitsViewport(fits)
    }
    checkFit()
    const observer = new ResizeObserver(checkFit)
    if (childrenRef.current) observer.observe(childrenRef.current)
    window.addEventListener("resize", checkFit)
    return () => {
      observer.disconnect()
      window.removeEventListener("resize", checkFit)
    }
  }, [isReportVariant])

  const scrollToOlderReports = (e: React.MouseEvent) => {
    e.preventDefault()
    const el = document.getElementById(OLDER_REPORTS_SECTION_ID)
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const scrollToContent = (e: React.MouseEvent) => {
    e.preventDefault()
    if (!scrollTargetId) return
    const el = document.getElementById(scrollTargetId)
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const startReading = (e: React.MouseEvent) => {
    if (isReportVariant || startTransitioning || typeof window === "undefined")
      return
    e.preventDefault()
    setStartTransitioning(true)
    window.sessionStorage.setItem(REPORT_TRANSITION_FLAG_KEY, "1")
    setTimeout(() => {
      if (typeof window !== "undefined") {
        const targetPath = getLocalizedStartReadingPath()
        const spaNavigate = window.___navigate
        if (typeof spaNavigate === "function") {
          spaNavigate(targetPath)
        } else {
          window.location.assign(targetPath)
        }
      }
    }, START_READING_TRANSITION_MS)
  }
  const hasTitleSplit = title?.includes(":")
  const titleParts = hasTitleSplit
    ? (() => {
        const [head, ...tail] = title.split(":")
        return {
          head: head.trim(),
          tail: tail.join(":").trim(),
        }
      })()
    : null
  const gradientClass =
    (isReportVariant || startTransitioning ? "zinc-900" : gradientFrom) ===
    "zinc-900"
      ? "from-zinc-900 via-zinc-900/85"
      : "from-zinc-800 via-zinc-900/85"
  const heroTopOffsetClass = isReportVariant
    ? "pt-20 sm:pt-24 lg:pt-36"
    : startTransitioning
      ? "pt-20 sm:pt-24 lg:pt-36"
      : "pt-24 sm:pt-28 lg:pt-52 2xl:pt-80"

  const imageAnimationClass =
    !isReportVariant && startTransitioning
      ? "opacity-60"
      : imageAnimate
        ? imageVisible
          ? "translate-y-0 opacity-60"
          : "translate-y-12 opacity-0"
        : "opacity-60"

  return (
    <section
      className={`${HERO_SECTION_BASE_CLASS} ${heroTopOffsetClass}`}
      aria-label={isReportVariant ? "Report hero" : "Annual report cover"}
    >
      <StaticImage
        src="../../../images/annual-reports/annual-report-start.jpg"
        alt=""
        layout="fullWidth"
        placeholder="blurred"
        loading="eager"
        className={`!absolute ${isReportVariant ? "!left-0 !right-0 !top-0 !h-[100svh]" : "!inset-0"} transition-all duration-1000 ease-out ${imageAnimationClass}`}
        style={isReportVariant ? { height: "100svh" } : undefined}
        imgClassName="object-cover object-bottom"
      />
      <div className="absolute inset-0 bg-zinc-900/50" aria-hidden />
      <div
        className={`absolute inset-x-0 bottom-0 h-[38vh] min-h-[14rem] bg-gradient-to-t ${gradientClass} to-transparent`}
        aria-hidden
      />
      <div
        className={`${HERO_CONTENT_WRAPPER_CLASS} ${isReportVariant ? "flex-1" : ""}`}
        ref={contentWrapperRef}
      >
        <h1
          className={`${HERO_TITLE_CLASS} max-w-5xl leading-tight transition-all duration-700 ease-out ${
            !isReportVariant && startTransitioning
              ? "translate-y-0 opacity-95"
              : isReportVariant && !reportTitleSettled
                ? "translate-y-10 opacity-0"
                : "translate-y-0 opacity-100"
          }`}
          style={
            isReportVariant && reportEntryAnimating
              ? { transitionDuration: `${REPORT_TITLE_SETTLE_MS}ms` }
              : undefined
          }
        >
          {titleParts ? (
            <span className="inline-flex flex-col items-center gap-2">
              <span className="whitespace-pre-line md:whitespace-nowrap">{`${titleParts.head}:`}</span>
              <span className="text-[0.72em] font-semibold leading-tight text-white/85">
                {titleParts.tail}
              </span>
            </span>
          ) : (
            title
          )}
        </h1>
        {description ? (
          <p
            className={`${isReportVariant ? "mt-2" : "mt-4"} ${HERO_DESCRIPTION_BASE} ${
              descriptionFadeIn
                ? `transition-opacity duration-700 ease-out ${descriptionVisible ? "opacity-100" : "opacity-0"}`
                : ""
            } transition-all duration-700 ease-out ${
              startTransitioning
                ? "translate-y-2 opacity-0"
                : "translate-y-0 opacity-100"
            }`}
          >
            {description}
          </p>
        ) : null}
        {children ? (
          <div
            ref={childrenRef}
            className={`${isReportVariant ? "-mt-3 sm:-mt-5 2xl:mt-2" : ""} ${isReportVariant && contentFitsViewport ? "mb-auto" : ""} w-full will-change-[opacity,transform] transition-[opacity,transform] duration-[1200ms] ease-out ${
              bodyVisible && bodyFadeVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-1 opacity-0"
            }`}
          >
            {children}
          </div>
        ) : null}
        {noteText ? (
          <p className="mt-3 max-w-2xl text-sm italic text-zinc-300/80 sm:text-base">
            {noteText}
          </p>
        ) : null}
        {!isReportVariant && (
          <div
            className={`mt-3 flex flex-col items-center justify-center gap-4 transition-all duration-700 ease-out sm:flex-row ${
              startTransitioning
                ? "translate-y-2 opacity-0"
                : "translate-y-0 opacity-100"
            }`}
          >
            <button
              type="button"
              onClick={startReading}
              className="inline-flex items-center justify-center rounded-xl bg-kkm-green px-8 py-4 text-lg font-semibold text-zinc-900 shadow-lg transition hover:bg-kkm-green/90 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-zinc-900"
            >
              {startCoverCta}
            </button>
            {showOlderReportsButton && (
              <button
                type="button"
                onClick={scrollToOlderReports}
                className="inline-flex items-center justify-center rounded-xl border-2 border-white/80 bg-transparent px-8 py-4 text-lg font-semibold text-white transition hover:border-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-zinc-900"
              >
                {readOlderReports}
              </button>
            )}
          </div>
        )}
      </div>
      {isReportVariant && showScrollArrow && (
        <button
          type="button"
          onClick={scrollToContent}
          className={`absolute bottom-16 left-1/2 z-10 hidden -translate-x-1/2 rounded-full p-2 text-white/90 transition-opacity duration-700 ease-out hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-zinc-900 sm:block 2xl:bottom-20 ${
            scrollArrowVisible ? "opacity-100" : "opacity-0"
          }`}
          aria-label="Scroll down"
        >
          <ChevronDownIcon className="h-8 w-8 animate-[bounce-centered_1s_infinite] sm:h-10 sm:w-10" />
        </button>
      )}
    </section>
  )
}

export default Hero
